import React, { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost } from "../../services/api";
import ClinicIcon from "./ClinicIcon";
import StaticNotificationsFlyout, { StaticNotification } from "./apexo/StaticNotificationsFlyout";
import "./NotificationCenter.css";

/* ────────────────────────────────────────────
   Types
──────────────────────────────────────────── */
interface NcItem {
  id: string | number;
  title: string;
  subtitle?: string;
  date?: string;
  category: "recall" | "stock" | "online" | "lab" | "followup";
  rawData?: unknown;
}

type NcCategory = "all" | "recall" | "stock" | "online" | "lab" | "followup";

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
  onRecallContacted?: (id: string | number) => void;
  onOnlineChanged?: () => void;
}

/* ────────────────────────────────────────────
   Category config
──────────────────────────────────────────── */
const CATEGORY_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; color: string; iconColor: string }
> = {
  recall: {
    label: "Recalls",
    icon: <ClinicIcon name="recall" size={14} />,
    color: "rgba(245,158,11,0.15)",
    iconColor: "#f59e0b",
  },
  stock: {
    label: "Low Stock",
    icon: <ClinicIcon name="stock" size={14} />,
    color: "rgba(239,68,68,0.15)",
    iconColor: "#ef4444",
  },
  online: {
    label: "Online Appts",
    icon: <ClinicIcon name="online" size={14} />,
    color: "rgba(59,130,246,0.15)",
    iconColor: "#3b82f6",
  },
  lab: {
    label: "Pending Lab",
    icon: <ClinicIcon name="lab" size={14} />,
    color: "rgba(168,85,247,0.15)",
    iconColor: "#a855f7",
  },
  followup: {
    label: "Follow-ups",
    icon: <ClinicIcon name="history" size={14} />,
    color: "rgba(16,185,129,0.15)",
    iconColor: "#10b981",
  },
};

const SNOOZE_KEY = "nc-snoozed";

function getSnoozed(): Set<string> {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as { id: string; until: number }[];
    const now = Date.now();
    const active = parsed
      .filter((s) => s.until > now)
      .map((s) => s.id);
    return new Set(active);
  } catch {
    return new Set();
  }
}

function snoozeItem(id: string) {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    const parsed: { id: string; until: number }[] = raw ? JSON.parse(raw) : [];
    const filtered = parsed.filter((s) => s.id !== id);
    filtered.push({ id, until: Date.now() + 60 * 60 * 1000 }); // 1 hour
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(filtered));
  } catch {
    // ignore
  }
}

/* ────────────────────────────────────────────
   Component
──────────────────────────────────────────── */
export default function NotificationCenter({
  open,
  onClose,
  onRecallContacted,
  onOnlineChanged,
}: NotificationCenterProps) {
  const [items, setItems] = useState<NcItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState<NcCategory>("all");
  const [snoozed, setSnoozed] = useState<Set<string>>(getSnoozed);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const all: NcItem[] = [];
    try {
      // Recalls
      const recalls = (await apiGet("/api/recalls/pending").catch(() => [])) as Record<string, unknown>[];
      if (Array.isArray(recalls)) {
        recalls.forEach((r) =>
          all.push({
            id: String(r.id ?? r.patient_id ?? Math.random()),
            title: String(r.patient_name || r.reason || "Follow-up needed"),
            subtitle: String(r.reason || ""),
            date: String(r.date || ""),
            category: "recall",
            rawData: r,
          })
        );
      }
    } catch { /* no-op */ }

    try {
      // Online appointments
      const today = new Date();
      const toIso = (d: Date) => {
        const x = new Date(d);
        x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
        return x.toISOString().slice(0, 10);
      };
      const qs = new URLSearchParams();
      qs.set("date_from", toIso(today));
      qs.set("date_to", toIso(new Date(Date.now() + 14 * 86400000)));
      const appts = (await apiGet(`/api/reception/online-appointments?${qs}`).catch(() => [])) as Record<string, unknown>[];
      if (Array.isArray(appts)) {
        appts
          .filter((a) => ["BOOKED", "CONFIRMED", "RESCHEDULED"].includes(String(a.status || "").toUpperCase()))
          .forEach((a) =>
            all.push({
              id: `online-${a.id ?? a.appointment_id ?? Math.random()}`,
              title: String(a.patient_name || a.name || "Online appointment"),
              subtitle: String(a.status || ""),
              date: String(a.appointment_date || a.date || ""),
              category: "online",
              rawData: a,
            })
          );
      }
    } catch { /* no-op */ }

    try {
      // Low stock from inventory
      const inv = (await apiGet("/api/manager/inventory").catch(() => [])) as Record<string, unknown>[];
      if (Array.isArray(inv)) {
        inv
          .filter((item) => item.is_low_stock)
          .forEach((item) =>
            all.push({
              id: `stock-${item.id ?? item.item_id ?? Math.random()}`,
              title: String(item.name || item.item_name || "Stock item"),
              subtitle: `Current: ${item.quantity ?? "?"} ${item.unit ?? "units"}`,
              category: "stock",
              rawData: item,
            })
          );
      }
    } catch { /* no-op */ }

    setItems(all);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      void fetchAll();
      setSnoozed(getSnoozed());
    }
  }, [open, fetchAll]);

  // Sync snooze logic across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SNOOZE_KEY) {
        setSnoozed(getSnoozed());
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  const handleSnooze = (id: string) => {
    snoozeItem(id);
    setSnoozed(getSnoozed());
  };

  const handleRecallContacted = async (item: NcItem) => {
    try {
      await apiPost(`/api/recalls/${item.id}/contacted`, {});
      onRecallContacted?.(item.id);
      await fetchAll();
    } catch {
      // no-op
    }
  };

  const handleOnlineAction = async (item: NcItem, action: string) => {
    try {
      const raw = item.rawData as Record<string, unknown>;
      const apptId = raw?.id ?? raw?.appointment_id;
      if (apptId) {
        await apiPost(`/api/reception/online-appointments/${apptId}/status`, { status: action });
        onOnlineChanged?.();
        await fetchAll();
      }
    } catch {
      // no-op
    }
  };

  const filtered = items.filter(
    (item) => activeCategory === "all" || item.category === activeCategory
  );
  const visible = filtered.filter((item) => !snoozed.has(String(item.id)));

  const countByCategory = (cat: NcCategory) =>
    cat === "all"
      ? items.filter((i) => !snoozed.has(String(i.id))).length
      : items.filter((i) => i.category === cat && !snoozed.has(String(i.id))).length;

  const TABS: { key: NcCategory; label: string }[] = [
    { key: "all", label: "All" },
    { key: "recall", label: "Recalls" },
    { key: "online", label: "Online" },
    { key: "stock", label: "Stock" },
    { key: "lab", label: "Lab" },
    { key: "followup", label: "Follow-ups" },
  ];

  if (!open) return null;

  const flyoutNotifications: StaticNotification[] = visible.map(item => {
    const cfg = CATEGORY_CONFIG[item.category];
    const actions = [];
    
    if (item.category === "recall") {
      actions.push({
        label: "Contacted",
        action: () => handleRecallContacted(item)
      });
    }
    if (item.category === "online") {
      actions.push({
        label: "Confirm",
        action: () => handleOnlineAction(item, "CONFIRMED")
      });
      actions.push({
        label: "Cancel",
        action: () => handleOnlineAction(item, "CANCELLED")
      });
    }
    actions.push({
      label: "Snooze",
      action: () => handleSnooze(String(item.id))
    });

    return {
      id: String(item.id),
      title: item.title,
      body: item.subtitle || item.category,
      icon: cfg.icon,
      iconBgColor: cfg.iconColor,
      actions
    };
  });

  return (
    <StaticNotificationsFlyout
      open={open}
      onClose={onClose}
      notifications={flyoutNotifications}
      onAllowBrowserOptions={() => console.log('Enabling push notifications...')}
    />
  );
}
